/**
 * Utility functions for parsing dropdown options with "Label::Value" format support.
 *
 * Format examples:
 * - "HD Quality::hd"     → { label: "HD Quality", value: "hd" }
 * - "Standard::sd"       → { label: "Standard", value: "sd" }
 * - "Simple Option"      → { label: "Simple Option", value: "Simple Option" }
 * - "16:9"               → { label: "16:9", value: "16:9" } (no double colon = same label/value)
 * - "Widescreen::16:9"   → { label: "Widescreen", value: "16:9" }
 * - "URL::https://a.com" → { label: "URL", value: "https://a.com" }
 *
 * Double colon "::" is the separator; single colons are preserved in both label and value.
 */

export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Parse a single option line in "Label::Value" format.
 * If no double colon is present, both label and value will be the same.
 */
export function parseOptionLine(optionLine: string): DropdownOption {
  const trimmed = optionLine.trim();
  if (!trimmed) return { label: '', value: '' };

  const separatorIndex = trimmed.indexOf('::');
  if (separatorIndex === -1) {
    return { label: trimmed, value: trimmed };
  }

  const label = trimmed.substring(0, separatorIndex).trim();
  const value = trimmed.substring(separatorIndex + 2).trim();

  // Handle edge cases
  if (!label && !value) return { label: '', value: '' };
  if (!label) return { label: value, value };
  if (!value) return { label, value: label };

  return { label, value };
}

/**
 * Parse a newline-separated string of options.
 */
export function parseOptionsString(optionsString: string): DropdownOption[] {
  if (!optionsString) return [];
  return optionsString
    .split('\n')
    .map(parseOptionLine)
    .filter(opt => opt.value);
}

/**
 * Universal parser that handles string, array of strings, or array of objects.
 * Maintains backward compatibility with existing formats.
 */
export function parseDropdownOptions(options: string | unknown[] | null | undefined): DropdownOption[] {
  if (!options) return [];

  // Handle newline-separated string
  if (typeof options === 'string') {
    return parseOptionsString(options);
  }

  // Handle array
  if (Array.isArray(options)) {
    return options
      .map(opt => {
        if (typeof opt === 'string') {
          return parseOptionLine(opt);
        }
        if (opt && typeof opt === 'object') {
          const objOpt = opt as { value?: string; label?: string };
          if (objOpt.value) {
            return {
              value: objOpt.value,
              label: objOpt.label || objOpt.value
            };
          }
        }
        return { value: '', label: '' };
      })
      .filter(opt => opt.value);
  }

  return [];
}
